import React, { useState } from "react";
import { ModalForm, useModalDispatch } from "collections/Modal";
import {
  base64UrlString,
  Field,
  FieldProps,
  Form,
  shortCodeString,
  TextArea
} from "collections/Form";
import { InferType, object } from "yup";
import i18n, { useTranslation } from "utilities/i18n";
import {
  useProcessAltLicenseRequestMutation,
  useProcessLicenseRequestMutation
} from "generated/graphql";
import { DISMISS, useNotification } from "collections/Notifications";
import { InstanceModel } from "utilities/licenseServerUtils";
import { useFormikContext } from "formik";
import { FormikHelpers } from "formik/dist/types";
import {
  CancelButton,
  CopyToClipboardButton,
  DetailsLoader,
  SubmitButton
} from "elements";
import { PartialExcept } from "utilities/types";

enum Step {
  REQUEST = "REQUEST",
  RESPONSE = "RESPONSE"
}

const ProcessLicenseNormalRequestSchema = object().shape({
  requestValue: base64UrlString()
    .trim()
    .label(i18n.t("ENCODED_REQUEST"))
    .required()
});

const ProcessAltLicenseRequestSchema = object().shape({
  requestValue: shortCodeString()
    .trim()
    .label(i18n.t("ENCODED_REQUEST"))
    .required()
});

export type ProcessLicenseRequestFormProps = InferType<
  typeof ProcessLicenseNormalRequestSchema
>;

const ProcessLicenseResponseSchema = object().shape({
  responseValue: base64UrlString()
    .trim()
    .label(i18n.t("ENCODED_RESPONSE"))
    .required()
});

export type ProcessLicenseResponseFormProps = InferType<
  typeof ProcessLicenseResponseSchema
>;

export const ProcessLicenseRequestModal: React.FC<{
  instances: Array<InstanceModel>;
}> = ({ instances }) => {
  const { t } = useTranslation();
  const [, processAltLicenseRequest] = useProcessAltLicenseRequestMutation();
  const [, processLicenseRequest] = useProcessLicenseRequestMutation();
  const closeModal = useModalDispatch();
  const [addNotification] = useNotification();
  const [step, setStep] = useState<Step>(Step.REQUEST);

  const first: PartialExcept<InstanceModel, "recordId" | "identifiableName"> =
    instances?.length > 0
      ? instances[0]
      : {
          recordId: "",
          useAlternateLicencing: { data: undefined },
          identifiableName: ""
        };
  const useAlternateLicencing = !!first?.useAlternateLicencing?.data;
  const identifiableName = first?.identifiableName;

  const next = () => {
    setStep(Step.RESPONSE);
  };

  const onSubmitRequest = async (
    values: ProcessLicenseRequestFormProps,
    formikHelpers: FormikHelpers<ProcessLicenseRequestFormProps>
  ) => {
    if (useAlternateLicencing) {
      const licenseResponse = await processAltLicenseRequest({
        recordId: first.recordId,
        requestValue: values.requestValue
      });

      if (licenseResponse.data?.processAltLicenseRequest) {
        formikHelpers.setFieldValue(
          "responseValue",
          licenseResponse.data?.processAltLicenseRequest
        );
        next();

        return;
      }

      if (licenseResponse.error) {
        addNotification({
          type: "error",
          dismiss: DISMISS.DEFAULT,
          message: t("PROCESS_ALT_LICENSE_ERROR", {
            message: licenseResponse.error.message
          })
        });

        return;
      }
    }

    const licenseResponse = await processLicenseRequest({
      recordId: first.recordId,
      requestValue: values.requestValue
    });

    if (licenseResponse.data?.processLicenseRequest) {
      formikHelpers.setFieldValue(
        "responseValue",
        licenseResponse.data?.processLicenseRequest
      );
      next();

      addNotification({
        type: "success",
        dismiss: DISMISS.DEFAULT,
        message: t("SUCCESSFULLY_PROCESSED_LICENSE_REQUEST"),
        persistThroughNav: true
      });

      return;
    }

    if (licenseResponse.error) {
      addNotification({
        type: "error",
        dismiss: DISMISS.DEFAULT,
        message: t("PROCESS_LICENSE_ERROR", {
          message: licenseResponse.error.message
        })
      });
    }
  };

  if (step === Step.REQUEST) {
    return (
      <ModalForm
        title={t("PROCESS_LICENSE_REQUEST")}
        initialValues={{ requestValue: "" }}
        onSubmit={onSubmitRequest}
        name={"ProcessLicenseRequestModal"}
        validationSchema={
          useAlternateLicencing
            ? ProcessAltLicenseRequestSchema
            : ProcessLicenseNormalRequestSchema
        }
      >
        <Form>
          <ProcessRequest closeModal={closeModal} />
        </Form>
      </ModalForm>
    );
  }

  if (step === Step.RESPONSE) {
    return (
      <ModalForm
        title={t("PROCESS_LICENSE_REQUEST")}
        initialValues={{ responseValue: "" }}
        onSubmit={closeModal}
        name={"ProcessLicenseRequestModal"}
        validationSchema={ProcessLicenseResponseSchema}
      >
        <Form>
          <ProcessResponse
            identifiableName={identifiableName}
            closeModal={closeModal}
          />
        </Form>
      </ModalForm>
    );
  }

  return null;
};

const ProcessResponse: React.FC<{
  identifiableName: string;
  closeModal: () => void;
}> = ({ identifiableName, closeModal }) => {
  const { t } = useTranslation();
  const { values, isSubmitting } = useFormikContext<
    ProcessLicenseResponseFormProps
  >();
  return (
    <>
      <DetailsLoader
        loading={isSubmitting}
        title={t("PROCESSING_LICENSE_RESPONSE")}
      >
        <div className="form-content">
          <label htmlFor="editor" className="sr-only-ignore">
            {t("ENCODED_RESPONSE")}
          </label>
          <TextArea
            id="editor"
            name="responseValue"
            errorAbove={true}
            className="details"
            readOnly={true}
          />
        </div>
      </DetailsLoader>
      <div className="btn-container">
        <CopyToClipboardButton
          name="responseValue"
          id="responseValue"
          notifyName={t("INSTANCE_LICENSE", {
            name: identifiableName
          })}
          getTextToCopy={() => values.responseValue}
          className="btn"
        />
        <CancelButton onClick={closeModal} />
        <Field name="submit">
          {({ form: { dirty, isSubmitting, isValid } }: FieldProps) => (
            <SubmitButton
              isSubmitting={isSubmitting}
              disabled={!isValid || !dirty}
              labelKey="DONE"
              submittingLabelKey="DONE"
            />
          )}
        </Field>
      </div>
    </>
  );
};

const ProcessRequest: React.FC<{ closeModal: () => void }> = ({
  closeModal
}) => {
  const { t } = useTranslation();
  const { isSubmitting } = useFormikContext();
  return (
    <>
      <DetailsLoader
        loading={isSubmitting}
        title={t("PROCESSING_LICENSE_REQUEST")}
      >
        <label htmlFor="editor" className="sr-only-ignore">
          {t("ENCODED_REQUEST")}
        </label>
        <TextArea
          id="editor"
          name="requestValue"
          errorAbove={true}
          className="details"
        />
      </DetailsLoader>
      <div className="btn-container">
        <CancelButton onClick={closeModal} />
        <Field name="submit">
          {({ form: { dirty, isSubmitting, isValid } }: FieldProps) => (
            <SubmitButton
              isSubmitting={isSubmitting}
              disabled={!isValid || !dirty}
              labelKey="PROCESS_LICENSE_REQUEST"
              submittingLabelKey="PROCESSING"
            />
          )}
        </Field>
      </div>
    </>
  );
};
